/**
 * Configuration centralisée de la navigation du layout.
 * Extensible : ajouter des items ici pour les nouveaux modules.
 */
import { ROUTES } from '@/constants/routes';
import type { MenuKey } from '@/types/permissions';
import {
  ArchiveBoxIcon,
  BanknotesIcon,
  BriefcaseIcon,
  CalculatorIcon,
  ChartBarIcon,
  CreditCardIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  HomeIcon,
  PhotoIcon,
  ReceiptRefundIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  ClockIcon,
  EnvelopeIcon,
  SparklesIcon,
  TruckIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface SidebarItem {
  name: string;
  icon: IconComponent;
  action: () => void;
  permissionKey: MenuKey;
  /** Chemin pour prefetch au hover (ne modifie pas l'UI) */
  prefetchPath?: string;
}

export interface NavSubItem {
  name: string;
  icon: IconComponent;
  action: () => void;
  /** Chemin pour prefetch au hover (ne modifie pas l'UI) */
  prefetchPath?: string;
  /** Badge compteur optionnel (ex: nombre de factures à payer) */
  badge?: number;
  /** Permission individuelle du sous-item. Si absente, hérite du parent. */
  permissionKey?: MenuKey;
  /** Si vrai, rend un séparateur visuel au lieu d'un item cliquable. */
  isSeparator?: boolean;
}

export interface NavItemSimple {
  name: string;
  icon: IconComponent;
  action: () => void;
  type: 'simple';
  permissionKey: MenuKey;
  /** Chemin pour prefetch au hover (ne modifie pas l'UI) */
  prefetchPath?: string;
}

export interface NavItemDropdown {
  name: string;
  icon: IconComponent;
  type: 'dropdown';
  id: string;
  permissionKey: MenuKey;
  subItems: NavSubItem[];
}

export type NavItem = NavItemSimple | NavItemDropdown;

export interface NavPermissionSubItem {
  name: string;
  /** Clé effective : la sienne si le sous-item la déclare, sinon celle du parent. */
  permissionKey: MenuKey;
  /** true si ce sous-item a sa propre clé, contrôlable indépendamment du parent. */
  isOverride: boolean;
}

export interface NavPermissionNode {
  name: string;
  permissionKey: MenuKey;
  subItems?: NavPermissionSubItem[];
}

/**
 * Arborescence de permissions du menu Navbar, dérivée directement de navItems
 * (source unique de vérité, identique à ce que voient réellement les
 * utilisateurs) - pour affichage hiérarchique dans la matrice de permissions
 * (UserManagement.tsx) au lieu d'une liste plate déconnectée du vrai menu.
 * Le navigate passé à createLayoutConfig est un no-op : les actions ne sont
 * jamais invoquées ici, seule la structure (name/permissionKey/subItems) est lue.
 */
export function getNavPermissionTree(): NavPermissionNode[] {
  const { navItems } = createLayoutConfig(() => {});
  return navItems.map(item => ({
    name: item.name,
    permissionKey: item.permissionKey,
    subItems:
      item.type === 'dropdown'
        ? item.subItems
            .filter(sub => !sub.isSeparator)
            .map(sub => ({
              name: sub.name,
              permissionKey: sub.permissionKey ?? item.permissionKey,
              isOverride: sub.permissionKey != null,
            }))
        : undefined,
  }));
}

export function createLayoutConfig(navigate: (path: string) => void) {
  return {
    sidebarItems: [
      {
        name: 'Menu Principal',
        icon: HomeIcon,
        action: () => navigate(ROUTES.HOME),
        permissionKey: 'home' as MenuKey,
        prefetchPath: ROUTES.HOME,
      },
      {
        name: 'Bon de commande',
        icon: DocumentTextIcon,
        action: () => navigate(ROUTES.ORDERS.BASE),
        permissionKey: 'orders' as MenuKey,
      },
      {
        name: 'Articles',
        icon: CubeIcon,
        action: () => navigate(ROUTES.ARTICLES),
        permissionKey: 'products' as MenuKey,
        prefetchPath: ROUTES.ARTICLES,
      },
      {
        name: 'Clients',
        icon: UserGroupIcon,
        action: () => navigate(ROUTES.CLIENTS),
        permissionKey: 'clients' as MenuKey,
        prefetchPath: ROUTES.CLIENTS,
      },
      {
        name: 'Livraisons',
        icon: TruckIcon,
        action: () => navigate(ROUTES.DELIVERIES.BASE),
        permissionKey: 'deliveries' as MenuKey,
      },
      {
        name: 'Statistique',
        icon: ChartBarIcon,
        action: () => navigate(ROUTES.PERIOD_STATISTICS),
        permissionKey: 'statistics' as MenuKey,
      },
      {
        name: 'Calculatrice',
        icon: CalculatorIcon,
        action: () => {}, // Géré par AppLayout (setCalculatorOpen)
        permissionKey: 'accounting' as MenuKey,
      },
      {
        name: 'Acomptes',
        icon: BanknotesIcon,
        action: () => navigate(ROUTES.ACCOMPTE_INVOICES.LIST),
        permissionKey: 'payments' as MenuKey,
      },
      {
        name: 'Rapport du jour',
        icon: SparklesIcon,
        action: () => {}, // Géré par AppLayout (setReportModalOpen)
        permissionKey: 'ai_chat' as MenuKey,
      },
    ] as SidebarItem[],

    navItems: [
      {
        name: 'Menu Principal',
        icon: HomeIcon,
        action: () => navigate(ROUTES.HOME),
        type: 'simple' as const,
        permissionKey: 'menu_principal' as MenuKey,
        prefetchPath: ROUTES.HOME,
      },
      {
        name: 'Ventes',
        icon: ShoppingCartIcon,
        type: 'dropdown' as const,
        id: 'ventes',
        permissionKey: 'ventes' as MenuKey,
        subItems: [
          {
            name: 'Bons de commande 1',
            icon: DocumentTextIcon,
            action: () => navigate(ROUTES.ORDERS.BASE),
            prefetchPath: ROUTES.ORDERS.BASE,
            permissionKey: 'ventes_orders' as MenuKey,
          },
          {
            name: 'Bons de commande',
            icon: DocumentTextIcon,
            action: () => navigate(ROUTES.ORDERS2.BASE),
            prefetchPath: ROUTES.ORDERS2.BASE,
            permissionKey: 'ventes_orders2' as MenuKey,
          },
          {
            name: 'Devis',
            icon: DocumentDuplicateIcon,
            action: () => navigate('/devis'),
            prefetchPath: '/devis',
            permissionKey: 'ventes_devis' as MenuKey,
          },
          {
            name: 'Factures',
            icon: ReceiptRefundIcon,
            action: () => navigate(ROUTES.INVOICES.LIST),
            prefetchPath: ROUTES.INVOICES.LIST,
            permissionKey: 'ventes_factures' as MenuKey,
          },
          {
            name: 'Notes de crédit',
            icon: ReceiptRefundIcon,
            action: () => navigate(ROUTES.CREDIT_NOTES.BASE),
            prefetchPath: ROUTES.CREDIT_NOTES.BASE,
            permissionKey: 'ventes_credit_notes' as MenuKey,
          },
        ],
      },
      {
        name: 'Listes',
        icon: CubeIcon,
        type: 'dropdown' as const,
        id: 'listes',
        permissionKey: 'listes' as MenuKey,
        subItems: [
          {
            name: 'Clients',
            icon: UserGroupIcon,
            action: () => navigate(ROUTES.CLIENTS),
            prefetchPath: ROUTES.CLIENTS,
            permissionKey: 'listes_clients' as MenuKey,
          },
          {
            name: 'Fournisseurs',
            icon: TruckIcon,
            action: () => navigate(ROUTES.FOURNISSEURS),
            prefetchPath: ROUTES.FOURNISSEURS,
            permissionKey: 'listes_fournisseurs' as MenuKey,
          },
          {
            name: 'Articles',
            icon: CubeIcon,
            action: () => navigate(ROUTES.ARTICLES),
            prefetchPath: ROUTES.ARTICLES,
            permissionKey: 'listes_articles' as MenuKey,
          },
        ],
      },
      {
        name: 'Livraisons',
        icon: TruckIcon,
        action: () => navigate(ROUTES.DELIVERIES.BASE),
        type: 'simple' as const,
        permissionKey: 'livraisons' as MenuKey,
      },
      {
        name: 'Statistiques',
        icon: ChartBarIcon,
        type: 'dropdown' as const,
        id: 'statistiques',
        permissionKey: 'statistiques' as MenuKey,
        subItems: [
          {
            name: 'Statistiques par période',
            icon: ChartBarIcon,
            action: () => navigate(ROUTES.PERIOD_STATISTICS),
            permissionKey: 'statistiques_par_periode' as MenuKey,
          },
          {
            name: 'Statistiques par vendeur',
            icon: UserIcon,
            action: () => navigate('/statistiques-vendeur'),
            permissionKey: 'statistiques_par_vendeur' as MenuKey,
          },
          {
            name: 'CA du jour',
            icon: BanknotesIcon,
            action: () => navigate(ROUTES.DAILY_SALES),
            permissionKey: 'ca_du_jour' as MenuKey,
          },
        ],
      },
      {
        name: 'Gestion',
        icon: BriefcaseIcon,
        type: 'dropdown' as const,
        id: 'gestion',
        permissionKey: 'gestion' as MenuKey,
        subItems: [
          {
            name: "Toutes les factures d'achat",
            icon: ShoppingBagIcon,
            action: () => navigate(ROUTES.ACHATS),
            prefetchPath: ROUTES.ACHATS,
            permissionKey: 'gestion_achats' as MenuKey,
          },
          {
            name: 'Commander des marchandises',
            icon: EnvelopeIcon,
            action: () => navigate(ROUTES.SUPPLIER_ORDERS),
            prefetchPath: ROUTES.SUPPLIER_ORDERS,
            permissionKey: 'gestion_commander_marchandises' as MenuKey,
          },
          {
            name: 'Commandes en attente',
            icon: ClockIcon,
            action: () => navigate(ROUTES.PENDING_SUPPLIER_ORDERS),
            prefetchPath: ROUTES.PENDING_SUPPLIER_ORDERS,
            permissionKey: 'gestion_commandes_attente' as MenuKey,
          },
          {
            name: 'Paiements fournisseurs',
            icon: BanknotesIcon,
            action: () => navigate(ROUTES.PURCHASE_PAYMENTS),
            prefetchPath: ROUTES.PURCHASE_PAYMENTS,
            permissionKey: 'gestion_paiements_fournisseurs' as MenuKey,
          },
          {
            name: 'Inventaire des articles',
            icon: ArchiveBoxIcon,
            action: () => navigate(ROUTES.INVENTAIRE),
            prefetchPath: ROUTES.INVENTAIRE,
            permissionKey: 'gestion_inventaire' as MenuKey,
          },
          {
            name: 'Inventaire 2023',
            icon: ArchiveBoxIcon,
            action: () => navigate(ROUTES.INVENTAIRE_2023),
            prefetchPath: ROUTES.INVENTAIRE_2023,
            permissionKey: 'gestion_inventaire_2023' as MenuKey,
          },
        ],
      },
      {
        name: 'Comptabilité',
        icon: CalculatorIcon,
        type: 'dropdown' as const,
        id: 'comptabilite',
        permissionKey: 'comptabilite' as MenuKey,
        subItems: [
          {
            name: 'Bons de commande',
            icon: DocumentTextIcon,
            action: () => navigate(ROUTES.ORDERS2.BASE),
            prefetchPath: ROUTES.ORDERS2.BASE,
            permissionKey: 'comptabilite_orders2' as MenuKey,
          },
          {
            name: 'Acomptes',
            icon: CreditCardIcon,
            action: () => navigate(ROUTES.ACCOMPTE_INVOICES.LIST),
            permissionKey: 'comptabilite_acomptes' as MenuKey,
          },
          {
            name: 'Notes de crédit',
            icon: ReceiptRefundIcon,
            action: () => navigate(ROUTES.CREDIT_NOTES.BASE),
            prefetchPath: ROUTES.CREDIT_NOTES.BASE,
            permissionKey: 'comptabilite_credit_notes' as MenuKey,
          },
          {
            name: 'Factures à payer',
            icon: BanknotesIcon,
            action: () => navigate(ROUTES.FACTURES_A_PAYER),
            prefetchPath: ROUTES.FACTURES_A_PAYER,
            permissionKey: 'comptabilite_factures_a_payer' as MenuKey,
          },
          {
            name: 'Comptabilité générale',
            icon: CalculatorIcon,
            action: () => console.log('Comptabilité générale - À implémenter'),
            permissionKey: 'comptabilite_generale' as MenuKey,
          },
        ],
      },
      {
        name: 'Marketing Studio',
        icon: PhotoIcon,
        action: () => navigate(ROUTES.MARKETING_STUDIO),
        type: 'simple' as const,
        permissionKey: 'marketing_studio' as MenuKey,
      },
    ] as NavItem[],
  };
}
